import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Card, CardVariantsTypes, Icon, Text } from '@vallourec-ui/vallourec-ui'

import { usePageTitle } from '../../hooks/usePageTitle'
import {
  Body,
  Container,
  IconContainer,
  OptionCardContainer,
  OptionsCardContainer,
} from './style'
import { PageProps } from '@/@types/global'

const Home: React.FC<PageProps> = ({ title, subtitle }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  usePageTitle({ title, subtitle })

  const homeCards = useMemo(
    () => [
      {
        id: 'generate-report',
        cardTitle: t('home.generateReport.title', 'Generate Report'),
        titleColor: 'neutral-color-100',
        cardSubtitle: t('home.generateReport.subtitle', 'Generate new reports'),
        subtitleColor: 'neutral-color-60',
        variant: 'white-24',
        icon: (
          <Icon
            name="FileListLine"
            color="neutral-color-0"
            width={28}
            height={28}
            align-items="flex-start"
          />
        ),
        onClick: () => navigate('/generate-report'),
      },
      {
        id: 'search-report',
        cardTitle: t('home.searchReport.title', 'Search Report'),
        titleColor: 'neutral-color-100',
        cardSubtitle: t(
          'home.searchReport.subtitle',
          'Search for existing reports'
        ),
        subtitleColor: 'neutral-color-60',
        variant: 'white-24',
        icon: (
          <Icon
            name="SearchReport"
            color="neutral-color-0"
            width={28}
            height={28}
            align-items="flex-start"
          />
        ),
        onClick: () => navigate('/search-report'),
      },
      {
        id: 'footer-settings',
        cardTitle: t('home.footerSettings.title', 'Configure Report Footer'),
        titleColor: 'neutral-color-100',
        cardSubtitle: t(
          'home.footerSettings.subtitle',
          'Set footer for each report'
        ),
        subtitleColor: 'neutral-color-60',
        variant: 'white-24',
        icon: (
          <Icon
            name="ReportFooter"
            color="neutral-color-0"
            width={28}
            height={28}
            align-items="flex-start"
          />
        ),
        onClick: () => navigate('/footer-settings'),
      },
      {
        id: 'remark-definition',
        cardTitle: t('home.remarkDefinition.title', 'Remark Definition'),
        titleColor: 'neutral-color-100',
        cardSubtitle: t(
          'home.remarkDefinition.subtitle',
          'Configure Remark Definition for each type'
        ),
        subtitleColor: 'neutral-color-60',
        variant: 'white-24',
        icon: (
          <Icon
            name="Remark"
            color="neutral-color-0"
            width={28}
            height={28}
            align-items="flex-start"
          />
        ),
        onClick: () => navigate('/remark-definition'),
      },
      {
        id: 'permissions-management',
        cardTitle: t('home.permissionsManagement.title', 'User Management'),
        titleColor: 'neutral-color-100',
        cardSubtitle: t(
          'home.permissionsManagement.subtitle',
          'User data and permissions'
        ),
        subtitleColor: 'neutral-color-60',
        variant: 'white-24',
        icon: (
          <Icon
            name="UserLine"
            color="neutral-color-0"
            width={28}
            height={28}
            align-items="flex-start"
          />
        ),
        onClick: () => navigate('/permissions-management'),
      },
      {
        id: 'search-report-orders',
        cardTitle: t('home.searchReportOrders.title', 'Search by Order'),
        titleColor: 'neutral-color-100',
        cardSubtitle: t(
          'home.searchReportOrders.subtitle',
          'Search report by Order'
        ),
        subtitleColor: 'neutral-color-60',
        variant: 'white-24',
        icon: (
          <Icon
            name="SearchLine"
            color="neutral-color-0"
            width={24}
            height={24}
            align-items="flex-start"
          />
        ),
        onClick: () => navigate('/search-report-orders'),
      },
      {
        id: 'approval-flow',
        cardTitle: t('home.approvalFlow.title', 'Approval Flow'),
        titleColor: 'neutral-color-100',
        cardSubtitle: t('home.approvalFlow.subtitle', 'Set approval flow'),
        subtitleColor: 'neutral-color-60',
        variant: 'white-24',
        icon: (
          <Icon
            name="ApprovalFlow"
            color="neutral-color-0"
            width={28}
            height={28}
            align-items="flex-start"
          />
        ),
        onClick: () => navigate('/approval-flow'),
      },
    ],
    [navigate, t]
  )

  return (
    <Container>
      <Body>
        {homeCards.map(
          ({
            id,
            icon,
            onClick,
            cardTitle,
            titleColor,
            variant,
            cardSubtitle,
            subtitleColor,
          }) => {
            return (
              <Card
                key={id}
                variant={variant as CardVariantsTypes}
                onClick={onClick}
              >
                <OptionsCardContainer>
                  <IconContainer>{icon}</IconContainer>
                  <OptionCardContainer>
                    <Text
                      color="neutral-color-100"
                      size="text-xl"
                      weight="semi-bold"
                    >
                      {t<string>(cardTitle)}
                    </Text>
                    <Text color="neutral-color-60" size="text-sm">
                      {t<string>(cardSubtitle)}
                    </Text>
                  </OptionCardContainer>
                </OptionsCardContainer>
              </Card>
            )
          }
        )}
      </Body>
    </Container>
  )
}

export default Home
